import { FC, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import SendIcon from "@mui/icons-material/Send";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Spinner from "../../components/Spinner";
import { useGetAccounts } from "../../hooks/useAccounts";
import { ACCOUNT_ID } from "../../utils/general";
import {
	useEmailPrograms,
	useEmailProgramPreview,
	useSendTestEmail,
} from "../../hooks/useEmailPrograms";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "../../domain/error.interface";

/**
 * Email programs — SIMS-9056.
 *
 * ─── THIS IS A STUB ──────────────────────────────────────────────────────
 *
 * It reads. It sends one test. It manages nothing, and the name is aspirational
 * on purpose — this is the seat the real thing will sit in.
 *
 * What exists today:
 *   - the catalogue of emails this product sends, from the server
 *   - a per-account preview: the document rendered with a CHOSEN account's
 *     branding, which is the part worth having early, because branding by
 *     account is the thing the old per-product templates could not express.
 *     The account is picked here rather than inferred from the session, so one
 *     operator can compare how the same email lands for several accounts
 *   - a test send, because no preview on a screen predicts Outlook, which
 *     renders through Word
 *
 * What this page is expected to grow into, and what to keep in mind when it
 * does:
 *
 *   - EDITING THE COPY. Subject, heading, body and button label per account,
 *     stored rather than compiled in. The catalogue is currently a TypeScript
 *     array (`libs/shared/src/email/programs.ts` in backend-2) precisely so
 *     that moving it into storage is a change of source, not of shape.
 *
 *   - CREATING A PROGRAM. New emails without a deploy. Note the constraint
 *     that shaped all of this: the messaging service keys templates by
 *     PRODUCT, so anything account-specific has to be composed on our side and
 *     passed through `DEFAULT/enUS/custom.html`. A "create template" feature
 *     here must not become a second template system in the other repo.
 *
 *   - ENABLING PER PROGRAM. SIMS-9058 adds the per-program switch for the
 *     call-request notification. When more emails become optional, that
 *     control belongs on this page rather than scattered across each feature's
 *     own settings screen.
 *
 *   - LAYOUT CHOICES. Logo placement, accent usage, a compact variant. The
 *     renderer already takes branding as data; a layout option would be one
 *     more field, not a new template.
 *
 *   - 🔴 IF COPY BECOMES EDITABLE, ESCAPING BECOMES A SECURITY CONTROL, NOT A
 *     DETAIL. The document is emitted verbatim by a triple-stache at the far
 *     end, so `escapeHtml` in the renderer is the only thing between a typed
 *     value and the recipient's mail client. Whatever editing lands here must
 *     keep sending PLAIN TEXT to the renderer. Do not add a rich-text field
 *     that forwards markup.
 *
 *   - 🔴 AND NO PARTICIPANT DATA IN A PREVIEW. A preview renders on demand for
 *     whoever opens this page. Sample content is illustrative and must stay
 *     that way; rendering a real participant's email here would put PHI on a
 *     screen that has no business showing it.
 * ─────────────────────────────────────────────────────────────────────────
 */
const EmailPrograms: FC = () => {
	const { showSnackBar } = useFeedBack();
	const { data: programs, isLoading, isError } = useEmailPrograms();

	const [selectedKey, setSelectedKey] = useState<string>("");
	const [testEmail, setTestEmail] = useState<string>("");
	// Defaults to the account this session belongs to, so the page opens on the
	// answer an operator most likely wants before touching anything.
	const [selectedAccountId, setSelectedAccountId] =
		useState<string>(ACCOUNT_ID);

	const { data: accounts, isLoading: isLoadingAccounts } = useGetAccounts();

	const selectableAccounts = (accounts ?? [])
		.filter((account) => !account.isDeleted)
		.sort((a, b) => a.name.localeCompare(b.name));

	// MUI warns (and renders blank) on a value that is not among the options, so
	// hold the control empty until the list confirms the account is in it.
	const accountValue = selectableAccounts.some(
		(account) => account._id === selectedAccountId,
	)
		? selectedAccountId
		: "";

	const selectedAccountName =
		selectableAccounts.find((account) => account._id === selectedAccountId)
			?.name ?? "this account";

	// Select the first program once the list arrives, so the preview pane is
	// never an empty panel asking to be clicked.
	useEffect(() => {
		if (!selectedKey && programs?.length) {
			setSelectedKey(programs[0].key);
		}
	}, [programs, selectedKey]);

	const {
		data: previewHtml,
		isLoading: isPreviewLoading,
		isError: isPreviewError,
	} = useEmailProgramPreview(
		selectedKey,
		selectedAccountId,
		!!selectedKey && !!selectedAccountId,
	);

	const sendTest = useSendTestEmail();
	const selected = programs?.find((program) => program.key === selectedKey);

	// Raised in review: the Send button used to enable on any non-empty value, so
	// a typo cost a round trip and came back as an error toast. The field is
	// already `type="email"`, which declared the intent without enforcing it —
	// nothing submits this as a form, so the browser's own validity check never
	// runs.
	//
	// Deliberately the same shape as the backend's check rather than something
	// stricter: this decides when a BUTTON lights up, and a client that refuses
	// an address the server would have accepted is a worse failure than one
	// round trip. The server's validation is the control; this is a courtesy.
	const testEmailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(
		testEmail.trim(),
	);

	const handleSendTest = (): void => {
		if (!selectedKey) return;

		sendTest.mutate(
			{
				key: selectedKey,
				email: testEmail.trim(),
				brandingAccountId: selectedAccountId,
			},
			{
				onSuccess: () => {
					// Names the account as well as the recipient: the same email sent
					// for two accounts differs only in branding, so a bare "sent"
					// leaves you unable to tell which one landed.
					showSnackBar(
						`Test email sent to ${testEmail.trim()} using ${selectedAccountName} branding`,
						"success",
					);
					setTestEmail("");
				},
				onError: (error) => {
					// The service rejects with the response body — but react-query types
					// the callback as Error, so the two have to be reconciled before the
					// message can be read.
					//
					// TWO shapes, and both have to be read or the message is lost. A
					// route that answers through `BaseResponse.buildError` nests it under
					// `error.errorMessage`; a thrown `HttpException` built with a string
					// is JSONed by `HttpExceptionFilter` as Nest's own
					// `{ statusCode, message, error }`, where `error` is the status TEXT
					// and the message sits at the top level. Reading only the first shape
					// swallowed every validation message this endpoint produces — the
					// operator saw the fallback and no reason. Same precedence as
					// `transformAxiosError`.
					const body = error as unknown as ErrorResponse & { message?: string };
					const message =
						body?.error?.errorMessage ??
						body?.message ??
						"The test email could not be sent.";
					showSnackBar(message, "error");
				},
			},
		);
	};

	if (isLoading) return <Spinner />;

	if (isError) {
		return (
			<Alert severity="error">
				The list of email programs could not be loaded.
			</Alert>
		);
	}

	return (
		<Box>
			<Typography variant="headlineSmall" sx={{ mb: 0.5, display: "block" }}>
				Email programs
			</Typography>
			<Typography
				variant="bodySmall"
				color="text.secondary"
				sx={{ mb: 3, display: "block" }}
			>
				The emails this product sends, shown with a chosen account&apos;s
				branding.
			</Typography>

			{/* Governs both panes, so it sits above them rather than inside either.
			    Changing it re-renders the preview and re-targets the test send —
			    the two must never disagree about whose branding is on screen. */}
			<FormControl sx={{ mb: 3, minWidth: 320 }} size="small">
				<InputLabel id="email-programs-account-label">Account</InputLabel>
				<Select
					labelId="email-programs-account-label"
					id="email-programs-account"
					label="Account"
					value={accountValue}
					disabled={isLoadingAccounts}
					onChange={(event) => setSelectedAccountId(event.target.value)}
				>
					{selectableAccounts.map((account) => (
						<MenuItem key={account._id} value={account._id}>
							{account.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
				<Box sx={{ flex: "1 1 320px", minWidth: 300 }}>
					<Card variant="outlined">
						<List disablePadding>
							{programs?.map((program, index) => (
								<Box key={program.key}>
									{index > 0 && <Divider />}
									<ListItemButton
										selected={program.key === selectedKey}
										onClick={() => setSelectedKey(program.key)}
									>
										<ListItemText
											primary={program.name}
											secondary={program.description}
											primaryTypographyProps={{ variant: "titleSmall" }}
											secondaryTypographyProps={{ variant: "bodySmall" }}
										/>
									</ListItemButton>
								</Box>
							))}
						</List>
					</Card>

					{selected && (
						<Card variant="outlined" sx={{ mt: 2 }}>
							<CardContent>
								<Typography variant="titleSmall" sx={{ display: "block" }}>
									Send a test
								</Typography>
								<Typography
									variant="bodySmall"
									color="text.secondary"
									sx={{ display: "block", mb: 2 }}
								>
									A preview on screen does not predict how a mail client renders
									it. Send one to yourself and open it where you read email.
								</Typography>

								<Stack direction="row" spacing={1} alignItems="flex-start">
									<TextField
										fullWidth
										size="small"
										type="email"
										label="Send to"
										placeholder="you@example.com"
										value={testEmail}
										onChange={(event) => setTestEmail(event.target.value)}
										disabled={sendTest.isPending}
										// Only once they have typed something: an error on an
										// untouched field is a telling-off, not a hint.
										error={!!testEmail.trim() && !testEmailIsValid}
										helperText={
											!!testEmail.trim() && !testEmailIsValid
												? "Enter a valid email address."
												: " "
										}
									/>
									<Button
										variant="contained"
										startIcon={<SendIcon />}
										disabled={!testEmailIsValid || sendTest.isPending}
										onClick={handleSendTest}
										sx={{ whiteSpace: "nowrap" }}
									>
										{sendTest.isPending ? "Sending…" : "Send"}
									</Button>
								</Stack>

								{/* Sent with the sample content below, never a real
								    participant's — said out loud so nobody assumes this is a
								    way to resend someone's actual notification. */}
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 1.5 }}
								>
									Sends the sample content shown here, with{" "}
									{selectedAccountName} branding, marked
									<Chip
										label="[Test]"
										size="small"
										sx={{ mx: 0.5, height: 18, fontSize: "0.65rem" }}
									/>
									in the subject.
								</Typography>
							</CardContent>
						</Card>
					)}
				</Box>

				<Box sx={{ flex: "2 1 560px", minWidth: 340 }}>
					<Card variant="outlined">
						<CardContent>
							<Typography variant="titleSmall" sx={{ display: "block" }}>
								Preview
							</Typography>
							<Typography
								variant="bodySmall"
								color="text.secondary"
								sx={{ display: "block", mb: 2 }}
							>
								{selected
									? `Subject: ${selected.subject}`
									: "Choose an email to preview."}
							</Typography>

							{isPreviewLoading && <Spinner />}

							{isPreviewError && (
								<Alert severity="error">
									This email could not be rendered for the selected account.
								</Alert>
							)}

							{/* Sandboxed, and srcDoc rather than a URL: the document is a
							    complete page with its own styles, so it needs its own
							    document — and nothing in it should ever run.

							    `aria-label` as well as `title`: the title attribute is the
							    accessible name for an iframe, and this one is rendered
							    through MUI's `Box component="iframe"` rather than a plain
							    element. Both name it identically, so the frame is announced
							    whatever MUI forwards. */}
							{!isPreviewLoading && !isPreviewError && previewHtml && (
								<Box
									component="iframe"
									title="Email preview"
									aria-label="Email preview"
									sandbox=""
									srcDoc={previewHtml}
									sx={{
										width: "100%",
										height: 620,
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 1,
										// 🔴 A literal white, deliberately NOT a theme token.
										// This surface is not part of our UI — it is a stand-in
										// for a mail client, and mail clients paint white. If it
										// followed our palette the preview would render the email
										// on a ground no coordinator will ever see it on, and in
										// dark mode it would show light-on-dark text that arrives
										// dark-on-white. The whole point of this pane is that it
										// does not lie about how the email looks.
										backgroundColor: "#ffffff",
									}}
								/>
							)}
						</CardContent>
					</Card>
				</Box>
			</Box>
		</Box>
	);
};

export default EmailPrograms;
